// src/components/WorkItemChatPanel.js
//
// Work Item Chat Panel
// ====================
//
// PURPOSE
// -------
// This component renders the chat thread for one universal work item key,
// for example:
//
//   "todo:123"
//   "leave:9223372036854775807"
//
// CURRENT ARCHITECTURE ALIGNMENT
// ------------------------------
// This component is intentionally aligned with your current auth/session model:
//
// 1) HTTP is authoritative
//    - initial thread load comes from HTTP
//    - sending messages happens via HTTP
//    - "read" and "seen" mutations happen via HTTP
//
// 2) Socket events are realtime hints
//    - socket message events may append a committed message quickly
//    - but they are NOT the source of truth for auth/session decisions
//
// 3) On 401 we must NOT hard logout the whole browser
//    - hard logout (/auth/logout) clears shared cookies
//    - that would affect other tabs/pages of the same shared user session
//    - therefore this component uses softLogout() only
//
// 4) This component should not own auth recovery
//    - apiClient owns refresh-on-401
//    - AuthContext owns local auth state
//    - ProtectedRoute owns route guarding
//
// UX / ROBUSTNESS GOALS
// ---------------------
// - abort pending HTTP when the component unmounts or key changes
// - deduplicate messages (important because the sender may receive both:
//     a) HTTP response from POST /chat/messages
//     b) socket broadcast of the same message)
// - keep message rendering safe even when user info is missing
// - best-effort mark chat as read and mark work item as seen
// - avoid duplicate sends while one send is in progress
// - scroll to bottom after:
//     * initial load
//     * new socket message
//     * successful send
//
// I18N NOTE
// ---------
// This component translates only frontend-owned UI labels, fallbacks and
// composer text. Backend-provided 4xx messages are still displayed as backend
// payload content, because those messages may contain business-specific access
// or validation context. Message content and raw work item keys are never
// translated.

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import AuthContext from "../context/AuthContext";

import {
  fetchWorkItemChat,
  sendWorkItemChatMessage,
  markWorkItemChatRead,
} from "../services/workItemChatService";

import { markWorkItemSeen } from "../services/workItemSeenService";
import { isRequestCanceled } from "../services/httpUtils";

import socketClient from "../utils/socketClient";
import { SOCKET_EVENTS } from "../utils/socketEvents";

import "./WorkItemChatPanel.css";

const DEV = process.env.NODE_ENV !== "production";

/* -------------------------------------------------------------------------- */
/* Small error helpers                                                         */
/* -------------------------------------------------------------------------- */

function isUnauthorizedError(err) {
  return err?.response?.status === 401;
}

function isForbiddenError(err) {
  return err?.response?.status === 403;
}

function isNotFoundError(err) {
  return err?.response?.status === 404;
}

/**
 * Build a user-facing error message.
 *
 * Rules:
 * - for 4xx backend errors, prefer backend message if present
 * - otherwise use the translated frontend fallback supplied by the caller
 */
function getNiceErrorMessage(err, fallback) {
  const status = err?.response?.status;
  const backendMsg =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    "";

  if (typeof status === "number" && status >= 400 && status < 500 && backendMsg) {
    return String(backendMsg);
  }

  return fallback;
}

/* -------------------------------------------------------------------------- */
/* Message normalization helpers                                               */
/* -------------------------------------------------------------------------- */

/**
 * Normalize one message object to the shape expected by the UI.
 *
 * Why this helper exists:
 * - backend responses may evolve
 * - some paths may use "content", others could later expose "body"
 * - we want one stable rendering contract in the component
 */
function normalizeMessage(raw) {
  if (!raw || typeof raw !== "object") return null;
  if (raw.id == null) return null;

  return {
    ...raw,
    content: raw.content ?? raw.body ?? "",
  };
}

/**
 * Normalize an array of messages.
 */
function normalizeMessages(list) {
  if (!Array.isArray(list)) return [];
  return list.map(normalizeMessage).filter(Boolean);
}

/**
 * Build a deterministic sort key for messages.
 *
 * We prefer timestamps if present, but keep the function safe:
 * - supports createdAt
 * - supports created_at
 * - falls back to 0 if missing
 */
function getMessageTimestamp(message) {
  const raw = message?.createdAt ?? message?.created_at ?? null;
  if (!raw) return 0;

  const d = new Date(raw);
  return Number.isFinite(d.getTime()) ? d.getTime() : 0;
}

/**
 * Merge messages by id and keep a stable order.
 *
 * This protects against:
 * - duplicate socket + HTTP copies of the same message
 * - minor ordering drift after merges
 */
function mergeMessages(existingList, incomingList) {
  const map = new Map();

  for (const msg of existingList || []) {
    const normalized = normalizeMessage(msg);
    if (normalized?.id != null) {
      map.set(String(normalized.id), normalized);
    }
  }

  for (const msg of incomingList || []) {
    const normalized = normalizeMessage(msg);
    if (normalized?.id != null) {
      map.set(String(normalized.id), normalized);
    }
  }

  return Array.from(map.values()).sort((a, b) => {
    const ta = getMessageTimestamp(a);
    const tb = getMessageTimestamp(b);

    if (ta !== tb) return ta - tb;

    return String(a.id).localeCompare(String(b.id));
  });
}

/* -------------------------------------------------------------------------- */
/* Author label helper                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Safe author label:
 * - regular message -> "First Last (username)"
 * - message without user -> translated "System" fallback
 *
 * In your backend this can happen for system-like or historical messages.
 */
function formatAuthor(message, t) {
  const user = message?.user || null;
  if (!user) return t("chatPanel.labels.system");

  const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
  const base = fullName || user.username || t("details.values.userNumber", { id: user.id });
  const usernamePart = user.username ? ` (${user.username})` : "";

  return `${base}${usernamePart}`;
}

/* -------------------------------------------------------------------------- */
/* Component                                                                   */
/* -------------------------------------------------------------------------- */

const WorkItemChatPanel = ({ workItemKey, variant = "embedded" }) => {
  const { authState, authChecked, softLogout } = useContext(AuthContext);
  const { t } = useTranslation("workItems");

  const myUserId = authState?.user?.id ?? null;

  /**
   * Keep a normalized current key.
   *
   * We use a string form everywhere to avoid accidental null/undefined drift.
   */
  const currentWorkItemKey = useMemo(() => {
    return String(workItemKey || "").trim();
  }, [workItemKey]);

  /**
   * Variant-based CSS class.
   */
  const rootClassName = useMemo(() => {
    return `wicp-root ${variant === "embedded" ? "wicp-embedded" : "wicp-page"}`;
  }, [variant]);

  /* ------------------------------------------------------------------------ */
  /* Refs                                                                      */
  /* ------------------------------------------------------------------------ */

  const isMountedRef = useRef(false);
  const threadRef = useRef(null);
  const loadAbortRef = useRef(null);
  const workItemKeyRef = useRef(currentWorkItemKey);

  useEffect(() => {
    workItemKeyRef.current = currentWorkItemKey;
  }, [currentWorkItemKey]);

  /* ------------------------------------------------------------------------ */
  /* Local state                                                               */
  /* ------------------------------------------------------------------------ */

  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");

  /* ------------------------------------------------------------------------ */
  /* Lifecycle guards                                                          */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;

      if (loadAbortRef.current) {
        loadAbortRef.current.abort();
        loadAbortRef.current = null;
      }
    };
  }, []);

  /* ------------------------------------------------------------------------ */
  /* Scroll helper                                                             */
  /* ------------------------------------------------------------------------ */

  /**
   * Scroll thread container to the bottom after the DOM settles.
   *
   * We intentionally do not scroll on every render.
   * Instead we call this after:
   * - initial load
   * - socket append
   * - local send success
   */
  const scrollToBottomSoon = useCallback(() => {
    setTimeout(() => {
      if (!isMountedRef.current) return;
      if (!threadRef.current) return;

      requestAnimationFrame(() => {
        if (!threadRef.current) return;
        threadRef.current.scrollTop = threadRef.current.scrollHeight;
      });
    }, 0);
  }, []);

  /* ------------------------------------------------------------------------ */
  /* Best-effort side effects                                                  */
  /* ------------------------------------------------------------------------ */

  /**
   * Mark chat as read for the current work item.
   *
   * This endpoint is idempotent, so best-effort usage is fine.
   */
  const markCurrentChatReadBestEffort = useCallback(() => {
    const key = workItemKeyRef.current;
    if (!key) return;

    markWorkItemChatRead(key).catch(() => {
      // intentionally ignored
    });
  }, []);

  /**
   * Mark work item as seen for the current work item.
   *
   * This is separate from "chat read" semantics in your architecture.
   */
  const markCurrentWorkItemSeenBestEffort = useCallback(() => {
    const key = workItemKeyRef.current;
    if (!key) return;

    markWorkItemSeen(key).catch(() => {
      // intentionally ignored
    });
  }, []);

  /* ------------------------------------------------------------------------ */
  /* Authoritative HTTP load                                                   */
  /* ------------------------------------------------------------------------ */

  /**
   * Load the chat thread from the backend.
   *
   * This is the authoritative source of truth for:
   * - thread existence
   * - full message history
   * - access permission enforcement
   */
  const loadChatAuthoritatively = useCallback(
    async (controller) => {
      setLoading(true);
      setError("");

      try {
        const data = await fetchWorkItemChat(currentWorkItemKey, {
          signal: controller.signal,
        });

        if (controller.signal.aborted || !isMountedRef.current) return;

        const nextMessages = normalizeMessages(data?.messages);

        setMessages(nextMessages);

        // Best-effort side effects after successful load.
        markCurrentChatReadBestEffort();
        markCurrentWorkItemSeenBestEffort();

        scrollToBottomSoon();
      } catch (err) {
        if (isRequestCanceled(err) || controller.signal.aborted) return;

        if (isUnauthorizedError(err)) {
          /**
           * CRITICAL:
           * local auth failure in THIS tab must not trigger backend logout
           * for all tabs/pages.
           */
          softLogout("WORK_ITEM_CHAT_LOAD_401");
          return;
        }

        if (!isMountedRef.current) return;

        if (isForbiddenError(err)) {
          setError(getNiceErrorMessage(err, t("chatPanel.errors.forbidden")));
          return;
        }

        if (isNotFoundError(err)) {
          setError(getNiceErrorMessage(err, t("chatPanel.errors.notFound")));
          return;
        }

        console.error("[WorkItemChatPanel] loadChatAuthoritatively failed:", err);
        setError(getNiceErrorMessage(err, t("chatPanel.errors.loadFailed")));
      } finally {
        if (!controller.signal.aborted && isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [
      currentWorkItemKey,
      markCurrentChatReadBestEffort,
      markCurrentWorkItemSeenBestEffort,
      scrollToBottomSoon,
      softLogout,
      t,
    ]
  );

  /**
   * Re-load whenever:
   * - auth bootstrap completed
   * - auth is valid
   * - work item key changed
   */
  useEffect(() => {
    if (!authChecked) return;
    if (!authState.isAuthenticated) return;

    if (!currentWorkItemKey) {
      setLoading(false);
      setMessages([]);
      setError(t("chatPanel.errors.invalidKey"));
      return;
    }

    if (loadAbortRef.current) {
      loadAbortRef.current.abort();
    }

    const controller = new AbortController();
    loadAbortRef.current = controller;

    loadChatAuthoritatively(controller);

    return () => {
      controller.abort();
    };
  }, [
    authChecked,
    authState.isAuthenticated,
    currentWorkItemKey,
    loadChatAuthoritatively,
    t,
  ]);

  /* ------------------------------------------------------------------------ */
  /* Socket realtime hints                                                     */
  /* ------------------------------------------------------------------------ */

  /**
   * Socket policy:
   * - connect best-effort
   * - join current work item room
   * - append committed messages when the backend emits them
   *
   * IMPORTANT:
   * This is not the primary source of truth.
   * It is only a realtime hint layer on top of HTTP.
   */
  useEffect(() => {
    if (!authChecked) return;
    if (!authState.isAuthenticated) return;
    if (!currentWorkItemKey) return;

    socketClient.connect();

    let cancelled = false;

    (async () => {
      try {
        const ack = await socketClient.joinWorkItem(currentWorkItemKey);

        if (cancelled) return;

        if (ack?.ok === false && DEV) {
          // Non-fatal: HTTP chat still works even if realtime room join is denied.
          // eslint-disable-next-line no-console
          console.warn("[WorkItemChatPanel] joinWorkItem denied:", ack);
        }
      } catch (err) {
        if (DEV) {
          // eslint-disable-next-line no-console
          console.warn(
            "[WorkItemChatPanel] joinWorkItem failed (non-fatal):",
            err?.message || err
          );
        }
      }
    })();

    const offMessage = socketClient.on(
      SOCKET_EVENTS.WORK_ITEM_CHAT_MESSAGE,
      ({ workItemKey: eventKey, message }) => {
        if (String(eventKey) !== String(workItemKeyRef.current)) return;

        const normalized = normalizeMessage(message);
        if (!normalized) return;

        setMessages((prev) => mergeMessages(prev, [normalized]));

        // Best-effort read update after receiving a committed message.
        markCurrentChatReadBestEffort();
        scrollToBottomSoon();
      }
    );

    return () => {
      cancelled = true;
      offMessage?.();

      // Best-effort leave. We intentionally do not await cleanup here.
      try {
        const p = socketClient.leaveWorkItem(currentWorkItemKey);
        if (p && typeof p.catch === "function") {
          p.catch(() => {});
        }
      } catch {
        // ignore
      }
    };
  }, [
    authChecked,
    authState.isAuthenticated,
    currentWorkItemKey,
    markCurrentChatReadBestEffort,
    scrollToBottomSoon,
  ]);

  /* ------------------------------------------------------------------------ */
  /* Send message                                                              */
  /* ------------------------------------------------------------------------ */

  /**
   * Send one message via HTTP.
   *
   * Why HTTP?
   * - backend validates access
   * - backend writes to DB
   * - backend emits socket event only AFTER commit
   */
  const onSend = useCallback(async () => {
    const content = String(text || "").trim();
    if (!content) return;
    if (sending) return;

    setSending(true);
    setError("");

    try {
      const created = await sendWorkItemChatMessage(currentWorkItemKey, content);

      const normalized = normalizeMessage(created);
      if (normalized) {
        setMessages((prev) => mergeMessages(prev, [normalized]));
      }

      setText("");

      // Sender has read the chat by definition.
      markCurrentChatReadBestEffort();
      scrollToBottomSoon();
    } catch (err) {
      if (isRequestCanceled(err)) return;

      if (isUnauthorizedError(err)) {
        softLogout("WORK_ITEM_CHAT_SEND_401");
        return;
      }

      console.error("[WorkItemChatPanel] onSend failed:", err);
      setError(getNiceErrorMessage(err, t("chatPanel.errors.sendFailed")));
    } finally {
      if (isMountedRef.current) {
        setSending(false);
      }
    }
  }, [
    currentWorkItemKey,
    text,
    sending,
    markCurrentChatReadBestEffort,
    scrollToBottomSoon,
    softLogout,
    t,
  ]);

  /**
   * Enter = send
   * Shift+Enter = newline
   */
  const onComposerKeyDown = useCallback(
    (event) => {
      if (event.key !== "Enter") return;
      if (event.shiftKey) return;

      event.preventDefault();
      onSend();
    },
    [onSend]
  );

  /* ------------------------------------------------------------------------ */
  /* Render helpers                                                            */
  /* ------------------------------------------------------------------------ */

  const title = useMemo(() => currentWorkItemKey, [currentWorkItemKey]);

  /* ------------------------------------------------------------------------ */
  /* Render gating                                                             */
  /* ------------------------------------------------------------------------ */

  if (!authChecked) {
    return (
      <div className={rootClassName}>
        {t("chatPanel.states.loadingAuth")}
      </div>
    );
  }

  if (!authState.isAuthenticated) {
    return (
      <div className={rootClassName}>
        {t("chatPanel.states.notAuthenticatedRedirecting")}
      </div>
    );
  }

  /* ------------------------------------------------------------------------ */
  /* Render                                                                    */
  /* ------------------------------------------------------------------------ */

  return (
    <div className={rootClassName}>
      {variant === "embedded" && (
        <div className="wicp-miniheader" title={title}>
          <div className="wicp-miniheader-title">{t("chatPanel.title")}</div>
          <div className="wicp-miniheader-subtitle">{title}</div>
        </div>
      )}

      {error && (
        <div className="wicp-error" role="alert" aria-live="polite">
          {error}
        </div>
      )}

      <div
        className="wicp-thread"
        ref={threadRef}
        aria-label={t("chatPanel.aria.thread")}
      >
        {loading ? (
          <div className="wicp-empty">{t("chatPanel.states.loadingChat")}</div>
        ) : messages.length === 0 ? (
          <div className="wicp-empty">{t("chatPanel.states.noMessages")}</div>
        ) : (
          messages.map((message) => {
            /**
             * Message is "mine" only when the author id exists and matches me.
             * Missing user_id should never be treated as "mine".
             */
            const mine =
              Boolean(myUserId) &&
              Boolean(message?.user_id) &&
              Number(message.user_id) === Number(myUserId);

            return (
              <div
                key={message.id}
                className={`wicp-msg ${mine ? "wicp-msg-mine" : ""}`}
              >
                <div className="wicp-msg-header">
                  <span className="wicp-author">
                    {formatAuthor(message, t)}
                  </span>
                </div>

                <div className="wicp-msg-body">
                  {message.content}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="wicp-composer">
        <textarea
          className="wicp-input"
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={onComposerKeyDown}
          placeholder={t("chatPanel.composer.placeholder")}
          disabled={sending}
          aria-label={t("chatPanel.composer.ariaInput")}
        />

        <button
          type="button"
          className="wicp-send"
          onClick={onSend}
          disabled={!text.trim() || sending}
        >
          {sending ? t("chatPanel.composer.sending") : t("chatPanel.composer.send")}
        </button>
      </div>
    </div>
  );
};

export default WorkItemChatPanel;
